// src/components/ShareBar.jsx
import React, { useRef } from "react";
import useStoryCard from "@/components/share/useStoryCard";
import StoryCard from "@/components/StoryCard";

const ShareBar = ({ destination, metrics }) => {
  const storyRef = useRef(null);
  const generateStoryCard = useStoryCard(storyRef);

  const handleDownloadStory = async (filenameSuffix = "story") => {
    const blob = await generateStoryCard();
    if (!blob) {
      alert("Failed to generate story card.");
      return null;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${destination.city}_${filenameSuffix}.png`;
    a.click();
    URL.revokeObjectURL(url);
    return blob;
  };

  const handleWhatsAppStatus = async () => {
    const blob = await handleDownloadStory("whatsapp_status");
    if (!blob) return;

    const caption = `Women Safety Index for ${destination.city}, ${destination.country} — SafeShe Atlas\n${window.location.href}`;
    try {
      await navigator.clipboard.writeText(caption);
    } catch {
      // ignore if clipboard not available
    }

    // Open WhatsApp app (cannot jump directly to Status)
    window.location.href = "whatsapp://";
    alert(
      "Story card downloaded and caption copied.\nOpen WhatsApp → My Status → pick the image from your gallery."
    );
  };

  const handleInstagramStory = async () => {
    const blob = await handleDownloadStory("instagram_story");
    if (!blob) return;

    // Try opening Instagram app (user will pick image from gallery)
    window.location.href = "instagram://";
    alert("Story card downloaded.\nOpen Instagram → Create Story → pick the image.");
  };

  const shareTextWhatsApp = () => {
    const text = `Check the Women Safety Score for ${destination.city}, ${destination.country}: ${window.location.href}`;
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
      "_blank"
    );
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${destination.city} – SafeShe Atlas`,
          text: `Women Safety Index for ${destination.city}`,
          url: window.location.href,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      alert("Native sharing is not supported on this device.");
    }
  };

  return (
    <>
      {/* OFF-SCREEN STORY CARD (must be rendered, not display:none) */}
      <div
        style={{
          position: "fixed",
          top: "-2000px",
          left: "-2000px",
          width: 1080,
          height: 1920,
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        <StoryCard
          ref={storyRef}
          city={destination.city}
          country={destination.country}
          score={metrics.composite_wsi?.toFixed(0) || "0"}
          imageUrl={destination.image_url}
        />
      </div>

      {/* Visible Share Bar */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-10 flex justify-center gap-6">
        {/* WhatsApp text link */}
        <button
          onClick={shareTextWhatsApp}
          className="p-4 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg transform hover:scale-105 transition"
          title="Share link on WhatsApp"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 32 32">
            <path d="M16 .4A15.6 15.6 0 0 0 2.2 24.9L.4 31.6l7.8-1.8A15.6 15.6 0 1 0 16 .4zm0 28.5a12.9 12.9 0 1 1 12.9-12.9A12.9 12.9 0 0 1 16 28.9zm7.5-9.7c-.4-.2-2.3-1.1-2.6-1.2s-.6-.2-.9.2-.9 1.2-1.1 1.4-.4.3-.8.1a10.7 10.7 0 0 1-5.1-4.4c-.3-.5-.3-.9 0-1.1s.5-.6.7-.9.3-.5.5-.9 0-.7 0-1-.9-2.3-1.3-3.1-.7-.7-.9-.7h-.8a1.6 1.6 0 0 0-1.2.6A5 5 0 0 0 8 13.2a9.6 9.6 0 0 0 4.5 7.9 20.5 20.5 0 0 0 3.8 1.7c1.5.5 2.8.4 3.8.2s2.3-1 2.6-1.9.3-1.7.2-1.9-.4-.2-.8-.4z" />
          </svg>
        </button>

        {/* Native share */}
        <button
          onClick={shareNative}
          className="p-4 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-lg transform hover:scale-105 transition"
          title="Share using device apps"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" />
          </svg>
        </button>

        {/* WhatsApp Status (story card) */}
        <button
          onClick={handleWhatsAppStatus}
          className="p-4 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg transform hover:scale-105 transition"
          title="Download story card & post to WhatsApp Status"
        >
          <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
            <path d="M7 2C4.2 2 2 4.2 2 7v10c0 2.8 2.2 5 5 5h10c2.8 0 5-2.2 5-5V7c0-2.8-2.2-5-5-5H7zm10 2a3 3 0 110 6 3 3 0 010-6zm-5 3a5 5 0 110 10 5 5 0 010-10z" />
          </svg>
        </button>

        {/* Instagram Story (story card) */}
        <button
          onClick={handleInstagramStory}
          className="p-4 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg transform hover:scale-105 transition"
          title="Download story card & post to Instagram Story"
        >
          <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
            <path d="M7 2C4.2 2 2 4.2 2 7v10c0 2.8 2.2 5 5 5h10c2.8 0 5-2.2 5-5V7c0-2.8-2.2-5-5-5H7zm10 2a3 3 0 110 6 3 3 0 010-6zm-5 3a5 5 0 110 10 5 5 0 010-10z" />
          </svg>
        </button>
      </div>
    </>
  );
};

export default ShareBar;
